import {
  Button,
  Col,
  ContactPersonDrawer,
  IconWrapper,
  PriorityBadge,
  Row,
  Skeleton,
  Spin,
  Tag,
  Typography,
} from 'components';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useClientTypeQuery } from 'api';
import { ADMIN_ROUTES } from 'constants/routes';
import { ContactInfoFieldKeyType } from 'typings/form';
import { OutlineArrowDownIcon } from 'components/common/icons';
import { ContactFieldRow } from 'components/common/ContactFieldRow';
import { useContactPersons } from './useContactPersons';
import detailsStyles from '../styles.module.scss';
import styles from './styles.module.scss';

export const ContactPersons: React.FC = () => {
  const { data: clientTypeData = [] } = useClientTypeQuery();
  const { loading, contactPersons, leadDetails, drawerVisible, toggleDrawer, toggleExpandPerson, isPersonExpanded } =
    useContactPersons();

  const clientType = useMemo(() => {
    if (leadDetails?.client?.clientTypeId && clientTypeData?.length) {
      return clientTypeData.find((i: { id: string }) => i.id === leadDetails.client.clientTypeId)?.name;
    }
    return '';
  }, [clientTypeData, leadDetails]);

  if (leadDetails.contactPerson && !contactPersons?.length) {
    if (loading) {
      return <Skeleton active />;
    }
    return null;
  }

  // Display only the first contact person in the main view
  const mainContactPerson = leadDetails.contactPerson || contactPersons[0];
  const contactEntries = Object.entries(mainContactPerson.contacts || {}) as [ContactInfoFieldKeyType, string[]][];

  return (
    <>
      <Col span={24} className={detailsStyles.main_info}>
        <Spin spinning={loading}>
          <Row>
            <Typography.Text className={'font-weight-600 font-size-16 margin-b-12'} style={{ color: '#05796B' }}>
              Client Information
            </Typography.Text>
          </Row>
          <Row justify={'space-between'} wrap={false}>
            <Col flex={'auto'}>
              <Link to={`${ADMIN_ROUTES.clients}/${leadDetails?.client?.id}/general`} target={'_blank'}>
                <Typography.Text underline ellipsis={{ tooltip: true }} className={styles.title}>
                  {leadDetails?.client?.name}
                </Typography.Text>
              </Link>
            </Col>
            {clientType && (
              <Col flex={'none'}>
                <Typography.Text>
                  <Tag className={styles.option_tag}>{clientType}</Tag>
                </Typography.Text>
              </Col>
            )}
          </Row>
          <Row style={{ marginBottom: 8 }} justify={'space-between'}>
            <PriorityBadge tooltip={leadDetails?.client?.priorityRationale} type={leadDetails?.client?.priorityLevel} />
          </Row>
          <Row>
            <Col span={24}>
              <Typography.Text className={styles.title}>Contact Person</Typography.Text>
            </Col>
          </Row>
          <Row gutter={[0, 4]} className={styles.contact_persons}>
            <Col span={24}>
              <Row justify={'space-between'} align={'middle'}>
                <Col>
                  <Row className={styles.name_flag_container} align={'middle'}>
                    <Typography.Text type={'secondary'}>{mainContactPerson.fullName}</Typography.Text>
                    {mainContactPerson.language?.flagLocation && (
                      <IconWrapper src={mainContactPerson.language?.flagLocation} />
                    )}
                  </Row>
                </Col>
              </Row>
            </Col>

            {mainContactPerson.department && (
              <Col span={24}>
                <Row justify={'space-between'}>
                  <Col>
                    <Typography.Text type={'secondary'}>Department</Typography.Text>
                  </Col>
                  <Col>
                    <Typography.Text type={'secondary'}>{mainContactPerson.department}</Typography.Text>
                  </Col>
                </Row>
              </Col>
            )}

            {mainContactPerson.role && (
              <Col span={24}>
                <Row justify={'space-between'}>
                  <Col>
                    <Typography.Text type={'secondary'}>Role</Typography.Text>
                  </Col>
                  <Col>
                    <Typography.Text type={'secondary'}>{mainContactPerson.role}</Typography.Text>
                  </Col>
                </Row>
              </Col>
            )}

            {/* Display up to 3 contact methods by default */}
            {contactEntries.slice(0, 3).map(([type, values]) => (
              <ContactFieldRow key={type} fieldValueMaxWidth={150} type={type} data={values} />
            ))}

            {/* "See All" button at the bottom */}
            {contactPersons.length > 1 && (
              <Col span={24}>
                <Row align={'middle'} justify={'end'}>
                  <Col>
                    <Button type={'link'} variant={'link'} className={styles.see_all_button} onClick={toggleDrawer}>
                      See All
                      <OutlineArrowDownIcon size={'sm'} color={'#308D82'} />
                    </Button>
                  </Col>
                </Row>
              </Col>
            )}
          </Row>
        </Spin>
      </Col>

      {/* Drawer to show all contact person */}
      <ContactPersonDrawer
        visible={drawerVisible}
        onClose={toggleDrawer}
        contactPersons={contactPersons}
        toggleExpandPerson={toggleExpandPerson}
        isPersonExpanded={isPersonExpanded}
      />
    </>
  );
};
